import cx from "classnames";
import { merge } from "lodash";

import { Menu, Select } from "@theorgchartdev/storybook";
import { GetDoc } from "@/Utils/StateLib";
import { FieldSelectListProps } from "@/Utils/ComponentLib";
import { getHoverTipContent } from "@/Utils/Misc";
import { appstr } from "@coreapp/utils/appstr";
import { comparefields } from "@coreapp/utils/comparefields";
import { QObjDBFieldDef } from "@coreapp/CoreApp/QObjDBFieldDef";
import { GetLayoutObjClass } from "../../Composites/LayoutHoverToolbar/Utils";
import { enumObjClassNames, kEmployeeCountFields } from "@coreapp/constants";
import { BuildSystemFieldOptions, enumSystemFieldsMode } from "@/Utils/DataLib";

const BuildFieldOption = (field, dataTestIdPrefix, clickHandler) => {
  const label = field instanceof QObjDBFieldDef ? field.GetFieldName() : field;
  const option = {
    label,
    fieldDef: field,
    value: field.GetFieldID?.() ?? field,
    clickHandler
  };
  if (dataTestIdPrefix) option.dataTestId = `${dataTestIdPrefix}-${label}`;
  return option;
};

/**
 *
 * @param {*} clickHandler
 * @param {*} dataTestIdPrefix
 * @param {*} additionalFields
 * @param {*} includeOptions
 * @param {*} objClass
 * @param {Array<object>} multiFieldOptions Array of multi-field(position and employee fields) options
 * @returns
 */
export const BuildFieldDefOptions = (
  clickHandler,
  dataTestIdPrefix = undefined,
  additionalFields = [],
  includeOptions = {},
  objClass = null,
  multiFieldOptions
) => {
  const doc = GetDoc();
  if (!doc) return [];

  let fieldOptions;
  if (multiFieldOptions) {
    fieldOptions = multiFieldOptions;
  } else {
    let fieldList = doc.GetFieldList2(
      includeOptions.calcs ?? true,
      includeOptions.readOnly ?? true,
      includeOptions.photos ?? true,
      includeOptions.locked ?? true,
      includeOptions.internal ?? false,
      includeOptions.aggregated ?? false,
      objClass
    );

    // Exclude internal underscore-prefixed calculation fields (e.g. _Headcount, _Peers,
    // _Span Of Control, _Subordinate Positions).  These are chart-builder internals and
    // must never appear in user-facing field pickers such as the box layout editor.
    fieldList = fieldList.filter(f => !f.GetFieldName?.()?.startsWith("_"));

    // For position-class boxes, hide employee-count calc fields (they live on holder/employee boxes)
    if (objClass?.GetClassName?.() === enumObjClassNames.position) {
      fieldList = fieldList.filter(f => !kEmployeeCountFields.includes(f.GetFieldName?.()));
    }

    fieldList = fieldList.concat(additionalFields);
    fieldList.sort(comparefields);

    fieldOptions = fieldList.map(f => BuildFieldOption(f, dataTestIdPrefix, clickHandler));
  }

  const hasFields = fieldOptions.length > 0;
  const options = [];

  // Divider sits on the last special option so the line renders between specials and fields.
  if (includeOptions.freetext) {
    options.push({
      label: appstr("freetext"),
      dataTestId: `${dataTestIdPrefix}-Free Text`,
      value: "freetext",
      clickHandler,
      divider: !includeOptions.none && hasFields
    });
  }

  if (includeOptions.none) {
    options.push({
      label: appstr("No Content"),
      value: "none",
      dataTestId: `${dataTestIdPrefix}-Blank`,
      clickHandler,
      divider: hasFields
    });
  }

  options.push(...fieldOptions);

  return options;
};

export const CellFieldsMenu = ({
  additionalFields = [],
  onClick,
  fieldListProps,
  customClass,
  dataTestIdPrefix,
  includeOptions,
  useSelect,
  enableMultiFieldMenu,
  ...otherProps
}) => {
  const objClass = GetLayoutObjClass();
  const Component = useSelect ? Select : Menu;
  const multiFieldOptions = enableMultiFieldMenu
    ? BuildSystemFieldOptions(
        onClick,
        {},
        enumSystemFieldsMode.filterable,
        dataTestIdPrefix ? dataTestIdPrefix : null,
        []
      )
    : null;

  return (
    <div className={cx("oc-fields-menu", customClass)}>
      <Component
        dataTestId={`${dataTestIdPrefix}-fields-menu`}
        options={BuildFieldDefOptions(
          onClick,
          dataTestIdPrefix,
          additionalFields,
          includeOptions,
          objClass,
          multiFieldOptions
        )}
        listProps={{
          ...FieldSelectListProps({
            inputProps: {
              placeholder: appstr("Search"),
              dataTestId: `${dataTestIdPrefix}-search`
            }
          }),
          enableVirtualization: false,
          // multiFieldMenu shows Employee/Position parents whose sub-lists already have their own search.
          enableSearch: !enableMultiFieldMenu,
          ...fieldListProps
        }}
        {...otherProps}
        triggerProps={merge(
          { tooltipProps: getHoverTipContent("Field selector") },
          otherProps.triggerProps
        )}
        tooltipProps={getHoverTipContent("Field selector")}
      />
    </div>
  );
};
